import dayjs, { Dayjs } from 'dayjs';

interface TooltipTitleParams {
  isProcessing: boolean;
  notStarted: boolean;
  failed: boolean;
  progress: number;
}

interface CellStyleParams {
  notStarted: boolean;
  isProcessing: boolean;
}

export const findByIdInChildrenOnly = (arr: any, targetId: string) => {
  for (const obj of arr) {
    if (obj.id === targetId) {
      return obj; // Match found
    }

    if (Array.isArray(obj.children)) {
      const found: any = findByIdInChildrenOnly(obj.children, targetId);
      if (found) return found;
    }
  }

  return null; // Not found
};

export const cellStyle = ({
  notStarted,
  isProcessing,
}: CellStyleParams): React.CSSProperties => ({
  cursor: notStarted || isProcessing ? 'not-allowed' : 'pointer',
  pointerEvents: notStarted || isProcessing ? 'none' : 'auto',
  opacity: notStarted || isProcessing ? 0.6 : 1,
});

export const getTooltipTitle = ({
  isProcessing,
  notStarted,
  failed,
  progress,
}: TooltipTitleParams): string => {
  if (isProcessing) {
    return `Processing: ${progress}%`;
  } else if (notStarted) {
    return 'Processing not yet started!';
  } else if (failed) {
    return 'Processing failed!';
  }
  return '';
};

const getFailedStatus = (createdDate: string): boolean => {
  const createdTime = dayjs(createdDate);
  const currentTime = dayjs();

  const timeDifferenceInMilliseconds = currentTime.diff(createdTime);

  const timeDifferenceInMinutes = timeDifferenceInMilliseconds / (1000 * 60);

  const isMoreThan15Minutes = timeDifferenceInMinutes > 15;

  return !isMoreThan15Minutes;
};

export const getProgressStatus = (row: any, contractStatus: any) => {
  const contractFound = contractStatus?.find(
    (item: any) => item?.id === row?.id
  );
  const progress = parseInt(contractFound?.percentage);
  const isProcessing = progress < 100;
  const notStarted =
    row?.status === 'Not Started' && contractFound?.status !== 'Done';
  const failed =
    row?.status === 'Failed' ||
    contractFound?.status === 'Failed' ||
    (!getFailedStatus(row?.created_on) &&
      contractFound?.status === 'Processing' &&
      parseInt(contractFound?.percentage) === 0);
  return { progress, isProcessing, notStarted, failed };
};

export const orderedKeys = [
  'contracts_count',
  // 'total_contracts_size',
  'active',
  // 'under_renewal',
  // 'under_amend',
  'expiring',
  'expiring_this_quarter',
  'expiring_next_quarter',
  'expired',
  'terminated',
];

export const tabsToColor = {
  contracts_count: '#e6e2f0',
  master_contracts: '#00acc114',
  under_renewal: '#00acc114',
  under_amend: '#88305F14',
  expired: '#ffefef',
  expiring: '#efe4a8db',
  expiring_this_quarter: '#f7eebfde',
  expiring_next_quarter: '#f7eebf70',
  active: '#deebff',
  terminated: '#dcdcdc',
} as const;

export const getBadgeColor = (activeHeader: string): string => {
  return tabsToColor[activeHeader as keyof typeof tabsToColor] || '';
};

export const handleOpenContract = (contractId: string) => {
  window.open('/document/documentlibrary/' + btoa(contractId), '_blank');
};

export const normPartyType = (type?: string) => {
  if (!type) return '';
  const partyType = type?.trim();
  if (partyType === 'Own_Party' || partyType === 'Own Party')
    return 'Own Party';
  if (partyType === 'Counter_Party' || partyType === 'Counterparty')
    return 'Counterparty';
  return partyType;
};

export function formatDurationParts(
  years: number,
  months: number,
  days: number
) {
  const parts: string[] = [];
  if (years > 0) {
    parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
  }
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? 'Month' : 'Months'}`);
  }
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'Day' : 'Days'}`);
  }

  return parts.join(' ');
}

export const normalize = (value?: any) =>
  (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const formatKey = (key: string) =>
  key
    ?.replace(/_/g, ' ')
    ?.trim()
    ?.replace(/\b\w/g, (c) => c.toUpperCase());

export const filterValueMap: Record<string, string> = {
  standstill_draft: 'Stand Still',
  my_drafts_under_collaboration: 'My Collaborating',
  drafts_under_collaboration: 'Collaborating',
  my_assigned_to_me: 'Assigned To Me',
};

export const getFilterKeyMap = (
  projectLabel?: string
): Record<string, string | undefined> => ({
  contractCategoryNames: 'Contract Category',
  contractTypeNames: 'Contract Name',
  contracting_parties: 'Parties',
  source_type: 'Source',
  territoryNames: 'Territory',
  projectsNames: projectLabel,
  locationNames: 'Locations',
  partyNames: 'Parties',
  name: 'Title',
});

export const DEFAULT_THIRD_LEVEL_EXCLUDES = [
  'filter_type',
  'sub_filter_type',
  'created_by',
  'contracttype',
  'contractcategory',
  'productmasters',
  'project',
  'territory',
  'ordering',
  'counterparties',
  'status_alias',
  'location',
  'contract_type',
  'product_master',
  'parties',
  'category',
  'ownparties',
  'users',
];

export function getThirdLevelFilters(
  payload: Record<string, any> | undefined,
  excludes: string[] = DEFAULT_THIRD_LEVEL_EXCLUDES
): [string, any][] {
  if (!payload) return [];
  return Object.entries(payload).filter(([key]) => !excludes.includes(key));
}

export const labelMap: any = {
  created: 'Uploaded On',
  start: 'Start Date',
  end: 'End Date',
};

export const formatThirdLevelFilters = (
  thirdLevelFilters: Array<[string, any]>
) => {
  return thirdLevelFilters?.map(([key, value]) => {
    if (key === 'date_filter_type') {
      return [key, labelMap[value] ?? value];
    }
    if (
      key === 'start_date' ||
      key === 'end_date' ||
      key === 'from_date' ||
      key === 'to_date'
    ) {
      return [key, dayjs(value).format('DD-MM-YYYY')];
    }
    if (Array.isArray(value)) {
      return [key, value];
    }
    return [key, String(value)];
  });
};

export const frequencyOptions = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
];

export const typeOptions = [
  { label: 'Recurring', value: 'recurring' },
  { label: 'One Time', value: 'one_time' },
];

export const rolesOptions = [
  { name: 'Owner', key: 'owner' },
  { name: 'Co-owner', key: 'coOwner' },
  { name: 'Senior Legal Reviewer', key: 'seniorLegalReviewer' },
  { name: 'Business Reviewer', key: 'businessReviewer' },
];

export const addRole = (
  map: Map<
    string,
    ('owner' | 'coOwner' | 'seniorLegalReviewer' | 'businessReviewer')[]
  >,
  userId: string,
  role: 'owner' | 'coOwner' | 'seniorLegalReviewer' | 'businessReviewer'
) => {
  const roles = map.get(userId) ?? [];
  if (!roles.includes(role)) {
    roles.push(role);
  }
  map.set(userId, roles);
};

export const getIsRoleAvailable = (user: any) => {
  if (!user) {
    return {
      owner: false,
      coOwner: false,
      seniorLegalReviewer: false,
      businessReviewer: false,
    };
  }
  return {
    owner: true,
    coOwner: user?.groups?.includes('/Legal') || false,
    seniorLegalReviewer:
      user?.roles?.includes('Senior Legal Reviewer') || false,
    businessReviewer: user?.roles?.includes('Business Reviewer') || false,
  };
};

export const normalizeDate = (
  value?: string | Dayjs | Date | null
): string | null => {
  if (!value) return null;
  // If already a Dayjs instance
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format('YYYY-MM-DD') : null;
  }
  // If Date object
  if (value instanceof Date) {
    return !isNaN(value.getTime()) ? dayjs(value).format('YYYY-MM-DD') : null;
  }
  // Must be string from here
  if (typeof value !== 'string') return null;
  // dd/mm/yyyy
  if (value.includes('/')) {
    const [dd, mm, yyyy] = value.split('/');
    return dayjs(`${yyyy}-${mm}-${dd}`).isValid()
      ? `${yyyy}-${mm}-${dd}`
      : null;
  }
  // yyyy-mm-dd or other ISO-like
  return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : null;
};

export const isValidDate = (value?: string | Dayjs | null) => {
  if (!value) return false;
  if (dayjs.isDayjs(value)) {
    return value.isValid();
  }
  if (typeof value === 'string') {
    if (value === 'Invalid Date') return false;
    return dayjs(value).isValid();
  }
  return false;
};
